import { apiClient } from "@/config/api.config";

/**
 * Service for handling admin-related API calls (experts, appointments, and categories)
 */
const adminService = {
  /**
   * Fetch all filters (or whatever "all" means in your API)
   * @returns {Promise<Object>} filters data
   */
  fetchExpertDetails: async (expertId, token) => {
    try {
      const response = await apiClient.get(
        `/appointments/experts/${expertId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to fetch expert details";
    }
  },
  updateExpertDetails: async (expertId, payload, token) => {
    try {
      const response = await apiClient.put(
        `/appointments/experts/${expertId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to update expert";
    }
  },

  fetchAllFilters: async () => {
    try {
      const response = await apiClient.get("/admin/all");
      return response.data; // assuming your API response has data in .data
    } catch (error) {
      throw error.response?.data?.message || "Failed to fetch filters";
    }
  },
  fetchAllContent: async ({ page = 1, limit = 10, filters = {} } = {}) => {
    try {
      // Build query params from page, limit and filters
      const params = { page, limit, ...filters };

      const response = await apiClient.get("/admin/allcontent", { params });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to fetch content";
    }
  },
  updateTextContent: async (id, editingContent, token) => {
    try {
      const formData = new FormData();

      // Append all fields including file if present
      Object.keys(editingContent).forEach((key) => {
        // Skip internal fields and file object
        if (!key.startsWith("_") && key !== "_formFile") {
          if (key === "tags" && Array.isArray(editingContent[key])) {
            formData.append(key, JSON.stringify(editingContent[key]));
          } else {
            formData.append(key, editingContent[key]);
          }
        }
      });

      // Handle file upload separately
      if (editingContent._formFile) {
        formData.append("file", editingContent._formFile);
      }

      const response = await apiClient.put(
        `/admin/updatetext/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error) {
      throw (
        error.response?.data?.message ||
        error.message ||
        "Failed to update content"
      );
    }
  },
  deleteTextContent: async (contentId, token) => {
    try {
      const response = await apiClient.delete(
        `/admin/deletetext/${contentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to delete content";
    }
  },

  /**
   * List experts with pagination and optional filters
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} [params.specialty] - Filter by specialty
   * @param {string} [params.status] - Filter by status
   * @returns {Promise<Object>} Experts data with pagination
   */
  listExperts: async ({ page = 1, limit = 10, specialty, status } = {}) => {
    try {
      const params = { page, limit };
      if (specialty) params.specialty = specialty;
      if (status) params.status = status;

      const response = await apiClient.get("/appointments/experts", { params });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to list experts";
    }
  },

  /**
   * Verify an expert
   * @param {string} expertId - ID of the expert to verify
   * @returns {Promise<Object>} Verification result
   */
  verifyExpert: async (expertId) => {
    try {
      const response = await apiClient.patch(
        `/appointments/experts/${expertId}/verify`,
        {}
      );
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to verify expert";
    }
  },

  /**
   * Suspend an expert
   * @param {string} expertId - ID of the expert to suspend
   * @returns {Promise<Object>} Suspension result
   */
  suspendExpert: async (expertId) => {
    try {
      const response = await apiClient.patch(
        `/appointments/experts/${expertId}/suspend`,
        {}
      );
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to suspend expert";
    }
  },

  /**
   * Update an expert
   * @param {string} expertId - ID of the expert to update
   * @param {Object} updatedData - Updated expert data
   * @returns {Promise<Object>} Update result
   */
  updateExpert: async (expertId, updatedData) => {
    try {
      const response = await apiClient.put(
        `/appointments/experts/${expertId}`,
        updatedData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to update expert";
    }
  },

  /**
   * Fetch all appointments sorted by scheduledAt
   * @param {Object} params - Query parameters
   * @param {string} params.sortBy - Field to sort by
   * @param {string} params.sortOrder - Sort order (asc/desc)
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @returns {Promise<Object>} Appointments data with pagination
   */
  listAppointments: async ({
    sortBy = "scheduledAt",
    sortOrder = "desc",
    page = 1,
    limit = 10,
  } = {}) => {
    try {
      const params = { sortBy, sortOrder, page, limit };
      const response = await apiClient.get("/appointments/appointments", {
        params,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to fetch appointments";
    }
  },

  /**
   * Create a new expert
   * @param {Object} expertData - Expert data to create
   * @returns {Promise<Object>} Creation result
   */
  createExpert: async (expertData) => {
    try {
      const response = await apiClient.post(
        "/appointments/experts",
        expertData
      );
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to create expert";
    }
  },

  /**
   * Get all categories including inactive ones
   * @returns {Promise<Array>} Array of categories
   */
  getAllCategories: async () => {
    try {
      const response = await apiClient.get("/blogs/category", {
        params: { includeInactive: true },
      });
      return response.data.data || [];
    } catch (error) {
      throw error.response?.data?.message || "Failed to fetch categories";
    }
  },

  /**
   * Create a new category
   * @param {Object} categoryData - Category data to create
   * @returns {Promise<Object>} Created category data
   */
  createCategory: async (categoryData) => {
    try {
      const response = await apiClient.post("/blogs/category", categoryData);

      if (!response.data.success) {
        throw new Error(
          response.data?.errors?.[0]?.message ||
            response.data.message ||
            "Creation failed"
        );
      }

      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to create category";
    }
  },

  /**
   * Update an existing category
   * @param {string} categoryId - ID of the category to update
   * @param {Object} categoryData - Updated category data
   * @returns {Promise<Object>} Updated category data
   */
  updateCategory: async (categoryId, categoryData) => {
    try {
      const response = await apiClient.put(
        `/blogs/category/${categoryId}`,
        categoryData
      );

      if (!response.data.success) {
        throw new Error(
          response.data?.errors?.[0]?.message ||
            response.data.message ||
            "Update failed"
        );
      }

      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to update category";
    }
  },

  /**
   * Delete a category
   * @param {string} categoryId - ID of the category to delete
   * @returns {Promise<Object>} Deletion result
   */
  deleteCategory: async (categoryId) => {
    try {
      const response = await apiClient.delete(`/blogs/category/${categoryId}`);

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to delete category");
      }

      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to delete category";
    }
  },

  /**
   * Get blog statistics
   * @returns {Promise<Object>} Blog statistics
   */
  getBlogStats: async () => {
    try {
      const response = await apiClient.get("/blogs/blogs/stats");
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to fetch blog stats";
    }
  },
};

/**
 * Service for handling tag-related API calls
 */
export const TagService = {
  /**
   * Fetch all tags
   * @param {Object} params - Query parameters
   * @param {string} [params.search] - Search term for filtering tags
   * @param {string} [params.sortBy="usage"] - Field to sort by
   * @param {string} [params.sortOrder="desc"] - Sort order (asc or desc)
   * @param {number} [params.limit=50] - Maximum number of tags to return
   * @returns {Promise} Promise with tags data
   */
  getAllTags: async (params = {}) => {
    try {
      const response = await apiClient.get("/blogs/tag", { params });
      return response.data.data || [];
    } catch (error) {
      throw error.response?.data?.message || "Failed to fetch tags";
    }
  },

  /**
   * Get popular tags
   * @param {number} [limit=10] - Maximum number of tags to return
   * @returns {Promise} Promise with popular tags data
   */
  getPopularTags: async (limit = 10) => {
    try {
      const response = await apiClient.get("/blogs/tag/popular", {
        params: { limit },
      });
      return response.data.data || [];
    } catch (error) {
      throw error.response?.data?.message || "Failed to fetch popular tags";
    }
  },

  /**
   * Get tag by slug
   * @param {string} slug - Tag slug
   * @returns {Promise} Promise with tag data and related blogs
   */
  getTagBySlug: async (slug) => {
    try {
      const response = await apiClient.get(`/blogs/tag/${slug}`);
      return response.data.data || {};
    } catch (error) {
      throw error.response?.data?.message || "Failed to fetch tag";
    }
  },

  /**
   * Create a new tag
   * @param {Object} tagData - Tag data
   * @returns {Promise} Promise with created tag data
   */
  createTag: async (tagData) => {
    try {
      const response = await apiClient.post("/blogs/tag", tagData);
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to create tag";
    }
  },

  /**
   * Update a tag
   * @param {string} tagId - Tag ID
   * @param {Object} tagData - Updated tag data
   * @returns {Promise} Promise with updated tag data
   */
  updateTag: async (tagId, tagData) => {
    try {
      const response = await apiClient.put(`/blogs/tag/${tagId}`, tagData);
      return response.data.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to update tag";
    }
  },

  /**
   * Delete a tag
   * @param {string} tagId - Tag ID
   * @returns {Promise} Promise with deletion status
   */
  deleteTag: async (tagId) => {
    try {
      const response = await apiClient.delete(`/blogs/tag/${tagId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || "Failed to delete tag";
    }
  },
};

export default adminService;
